<script setup>
import {onMounted, onUnmounted, ref} from "vue";
import {useSecurityStore} from "@/stores/Security";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import axios from "axios";


const {$i18n, $route, $swal} = useTools();
const {logout} = useSharedMethod();
const securityStore = useSecurityStore();
const drawer = ref(true);
const mini_variant = ref(false);

const items = ref([
    {
        action: "mdi-calendar-account",
        title: "Gestion Visites",
        active: ["physicians", "pharmacies", "wholesalers"].includes($route.name),
        access: securityStore.authorities['READ_PHYSICIAN'] ||
            securityStore.authorities['READ_PHARMACY'] ||
            securityStore.authorities['READ_WHOLESALER'],
        items: [
            {
                title: $i18n.t('module.physician'),
                action: "mdi-doctor",
                link: 'physicians',
                access: securityStore.authorities['READ_PHYSICIAN']
            },
            {
                title: $i18n.t('module.pharmacy'),
                action: "mdi-pill",
                link: 'pharmacies',
                access: securityStore.authorities['READ_PHARMACY']
            },
            {
                title: $i18n.t('module.wholesaler'),
                action: "mdi-point-of-sale",
                link: 'wholesalers',
                access: securityStore.authorities['READ_WHOLESALER']
            },
        ],
    },
    {
        action: "mdi-map-marker-path",
        title: $i18n.t('module.tour_plan'),
        active: ['tour_plans', 'tour_plan_calendar'].includes($route.name),
        access: true,
        items: [
            {
                action: "mdi-calendar-clock",
                title: "Calendrier",
                link: 'tour_plan_calendar',
                access: true
            },
            {
                action: "mdi-transit-connection-variant",
                title: "Listing",
                link: 'tour_plans',
                access: true
            },
        ]
    },
    {
        action: "mdi-wallet",
        title: $i18n.t('module.expense'),
        active: ['expenses'].includes($route.name),
        access: securityStore.authorities['READ_EXPENSE'],
        items: [
            {
                action: "mdi-cash",
                title: $i18n.t('module.expense'),
                link: 'expenses',
                access: securityStore.authorities['READ_EXPENSE']
            },
        ]
    },
    {
        action: "mdi-chart-box-multiple",
        title: "Rapports",
        active: ['activity_report', 'visits.physician', 'visits.pharmacy', 'visits.wholesaler'].includes($route.name),
        access: securityStore.authorities['READ_REPORT'],
        items: [
            {
                action: "mdi-home-analytics",
                title: $i18n.t('module.activity_report'),
                link: 'activity_report',
                access: securityStore.authorities['READ_REPORT'],
            },
            {
                action: "mdi-doctor",
                title: $i18n.t('module.visit_physician'),
                link: 'visits.physician',
                access: securityStore.authorities['READ_REPORT']
            },
            {
                action: "mdi-pill",
                title: $i18n.t('module.visit_pharmacy'),
                link: 'visits.pharmacy',
                access: securityStore.authorities['READ_REPORT']
            },
            {
                action: "mdi-point-of-sale",
                title: $i18n.t('module.visit_wholesaler'),
                link: 'visits.wholesaler',
                access: securityStore.authorities['READ_REPORT']
            },
        ],
    },
    {
        action: "mdi-monitor-dashboard",
        title: "Analyse",
        active: ['analysis.physician', 'analysis.pharmacy', 'analysis.wholesaler'].includes($route.name),
        access: securityStore.authorities['READ_ANALYSIS_PHYSICIAN'] ||
            securityStore.authorities['READ_ANALYSIS_PHARMACY'] ||
            securityStore.authorities['READ_ANALYSIS_WHOLESALER'],
        items: [
            {
                action: "mdi-doctor",
                title: $i18n.t('module.analysis_physician'),
                link: 'analysis.physician',
                access: securityStore.authorities['READ_ANALYSIS_PHYSICIAN']
            },
            {
                action: "mdi-pill",
                title: $i18n.t('module.analysis_pharmacy'),
                link: 'analysis.pharmacy',
                access: securityStore.authorities['READ_ANALYSIS_PHARMACY']
            },
            {
                action: "mdi-point-of-sale",
                title: $i18n.t('module.analysis_wholesaler'),
                link: 'analysis.wholesaler',
                access: securityStore.authorities['READ_ANALYSIS_WHOLESALER']
            },
        ],
    },
    {
        action: "mdi-content-duplicate",
        title: $i18n.t('module.deduplication'),
        active: ['deduplication.physician', 'deduplication.pharmacy', 'deduplication.wholesaler'].includes($route.name),
        access: securityStore.authorities['READ_DEDUPLICATION'],
        items: [
            {
                action: "mdi-doctor",
                title: $i18n.t('module.physician'),
                link: 'deduplication.physician',
                access: securityStore.authorities['READ_DEDUPLICATION']
            },
            {
                action: "mdi-pill",
                title: $i18n.t('module.pharmacy'),
                link: 'deduplication.pharmacy',
                access: securityStore.authorities['READ_DEDUPLICATION']
            },
            {
                action: "mdi-point-of-sale",
                title: $i18n.t('module.wholesaler'),
                link: 'deduplication.wholesaler',
                access: securityStore.authorities['READ_DEDUPLICATION']
            },
        ],
    },
    {
        action: "mdi-cog-outline",
        title: "Paramétres",
        active: ['promotion_campaigns', 'products', 'product_ranges', 'delegates', 'data_selects', 'roles'].includes($route.name),
        access: securityStore.authorities['READ_PARAM'],
        items: [
            {
                action: "mdi-bullseye-arrow",
                title: $i18n.t('module.promotion_campaign'),
                link: 'promotion_campaigns',
                access: securityStore.authorities['READ_PARAM']
            },
            {
                action: "mdi-package-variant-closed",
                title: $i18n.t('module.product_range'),
                link: 'product_ranges',
                access: securityStore.authorities['READ_PARAM']
            },
            {
                action: "mdi-pill-multiple",
                title: $i18n.t('module.product'),
                link: 'products',
                access: securityStore.authorities['READ_PARAM']
            },
            {
                action: "mdi-account-network",
                title: $i18n.t('module.delegate'),
                link: 'delegates',
                access: securityStore.authorities['READ_PARAM']
            },
            {
                action: "mdi-cable-data",
                title: $i18n.t('module.data_select'),
                link: 'data_selects',
                access: securityStore.authorities['READ_PARAM']
            },
            {
                action: "mdi-account-group-outline",
                title: $i18n.t("module.role"),
                link: "roles",
                access: securityStore.authorities['READ_PARAM'],
            },
        ],
    },
]);

let timerId = null;
let failCount = 0;
const MAX_FAILURES = 3;

const testConnection = async () => {
    try {
        const response = await axios.get('/auth/testConnection');
        if (response.data === 'LOGOUT') {
            logout();
        }
        return true;
    } catch (error) {
        if (!error.response) {
            return false; // network error, no connection
        }
        return true; // server responded (even with error) = still connected
    }
};

const checkConnection = async () => {
    const ok = await testConnection();
    if (ok) {
        failCount = 0;
    } else {
        failCount++;
        if (failCount >= MAX_FAILURES) {
            failCount = 0; // reset so it can re-trigger later if still failing
            // alert('Veuillez vérifier votre connexion');
            $swal('Veuillez vérifier votre connexion', '', 'error');
        }
    }
};

onMounted(() => {
    timerId = setInterval(checkConnection, 10000);
});

onUnmounted(() => {
    clearInterval(timerId);
    timerId = null;
});

</script>

<template>
    <div class="home">
        
        <v-app-bar
            height="35"
            app
            dense
        >
            <v-app-bar-nav-icon @click="mini_variant= !mini_variant"></v-app-bar-nav-icon>
            <v-toolbar-title>{{ $t("app_name") }}</v-toolbar-title>
            <v-spacer/>
        </v-app-bar>
        <nav>
            <v-navigation-drawer
                v-model="drawer"
                :mini-variant="mini_variant"
                width="250"
                app
            
            >
                <v-img
                    height="100"
                    :src="`${securityStore.getImageLink()}/drawer-background.jpg`"
                >
                    <v-row justify="center" align="center">
                        <v-col cols="10">
                            <v-list dense nav class="py-0">
                                <v-list-item two-line>
                                    <!--<v-list-item-avatar>
                                        <WorkerImage
                                            :worker-id="securityStore.userContext.workerId"
                                            disable-card
                                            readonly
                                        />
                                    </v-list-item-avatar>-->
                                    
                                    <v-list-item-content>
                                        <v-list-item-title class="white--text subtitle-1">
                                            <strong>{{ securityStore.userContext.fullName }}</strong>
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list>
                        </v-col>
                    </v-row>
                </v-img>
                <v-list dense nav>
                    <template
                        v-for="item in items"
                    >
                        <v-list-group
                            color="teal"
                            :key="item.title"
                            v-model="item.active"
                            v-show="item.access"
                            :prepend-icon="item.action"
                            no-action
                        >
                            <template v-slot:activator>
                                <v-list-item-content>
                                    <v-list-item-title v-text="item.title"></v-list-item-title>
                                </v-list-item-content>
                            </template>
                            
                            
                            <template v-for="(child, y) in item.items">
                                <v-list-item
                                    v-if="child.subGroups"
                                    :key="`child-${y}`"
                                    v-show="child.access"
                                    class="mb-n2 ml-n6"
                                >
                                    <v-list-item-content>
                                        <v-list-group
                                            :key="`subGroup-${y}`"
                                            color="teal"
                                            v-model="child.active"
                                            :prepend-icon="child.action"
                                            no-action
                                            sub-group
                                        >
                                            <template v-slot:activator>
                                                <v-list-item-content class="ml-4 mt-n3">
                                                    <v-list-item-title v-text="child.title"></v-list-item-title>
                                                </v-list-item-content>
                                            </template>
                                            <template v-for="(subGroup, z) in child.subGroups">
                                                <v-list-item
                                                    :key="`subGroup-${z}`"
                                                    :to="{ name: subGroup.link }"
                                                    v-show="subGroup.access"
                                                    link
                                                    :disabled="subGroup.disabled"
                                                >
                                                    <v-list-item-icon>
                                                        <v-icon color="teal lighten-1">{{ subGroup.action }}</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-content class="ml-4">
                                                        <v-list-item-title>
                                                            {{ subGroup.title }}
                                                        </v-list-item-title>
                                                    </v-list-item-content>
                                                </v-list-item>
                                            </template>
                                        </v-list-group>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item
                                    v-else
                                    :key="`child-${y}`"
                                    :to="{ name: child.link }"
                                    v-show="child.access"
                                    link
                                    :disabled="child.disabled"
                                >
                                    <v-list-item-icon>
                                        <v-icon color="teal lighten-1">{{ child.action }}</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            {{ child.title }}
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        
                        </v-list-group>
                    </template>
                
                </v-list>
                <template v-slot:append>
                    <v-divider/>
                    <v-row dense>
                        <v-col cols="12">
                            <v-list dense>
                                <!--<v-list-item
                                    @click="$router.push({ name: 'parameters' })"
                                >
                                    <v-list-item-action>
                                        <v-icon>mdi-cog-outline</v-icon>
                                    </v-list-item-action>
                                    <v-list-item-title>Paramétres</v-list-item-title>
                                </v-list-item>-->
                                <v-list-item @click="$router.push({ name: 'reset_password' })">
                                    <v-list-item-action>
                                        <v-icon>mdi-lock-reset</v-icon>
                                    </v-list-item-action>
                                    <v-list-item-title> Modifier mot de passe</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="logout">
                                    <v-list-item-action>
                                        <v-icon>mdi-logout</v-icon>
                                    </v-list-item-action>
                                    <v-list-item-title> Déconnecter</v-list-item-title>
                                </v-list-item>
                            
                            </v-list>
                        </v-col>
                    </v-row>
                </template>
            </v-navigation-drawer>
        </nav>
        <v-main>
            <transition name="scale-transition" hide-on-leave>
                <router-view/>
            </transition>
        </v-main>
    
    </div>
</template>
